import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning } from "@phosphor-icons/react";

export default function Register() {
  const { user, register, error } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await register(firstName.trim(), lastName.trim(), email, password);
    setSubmitting(false);
    if (ok) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <Lightning weight="fill" size={22} className="text-[#FF2A2A]" />
            <span className="font-heading font-black text-xl tracking-tight">
              RECRUITFLOW
            </span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Get started
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mb-10 leading-[1.05]">
            Create your<br />account.
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  First name
                </label>
                <input
                  data-testid="register-first-name-input"
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Last name
                </label>
                <input
                  data-testid="register-last-name-input"
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                  placeholder="Recruiter"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Email
              </label>
              <input
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Password
              </label>
              <input
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950 transition-colors"
                placeholder="At least 6 characters"
              />
            </div>

            {error && (
              <div
                className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-4 py-3 rounded-sm"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white py-3.5 rounded-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            Already registered?{" "}
            <Link to="/login" className="text-zinc-950 underline underline-offset-4 hover:no-underline" data-testid="goto-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-zinc-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/7135007/pexels-photo-7135007.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            Recruitment Deal Tracker
          </div>
          <div>
            <div className="font-heading font-black text-5xl tracking-tight leading-[1.05] mb-6">
              Built to close<br />
              more deals.
            </div>
            <div className="text-zinc-300 text-base max-w-md leading-relaxed">
              Log candidates, run interview slots, split fees, and chase clients on autopilot. A cleaner deal board built for recruiters.
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            v1.0 — Built for recruiters
          </div>
        </div>
      </div>
    </div>
  );
}
